import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Branch from 'components/Form/Branch';
import Layout from 'components/Form/PVQLocation/Layouts';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
} from 'types/props';
import PVQLocation from 'components/Form/PVQLocation';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import { repossessedPropertyTypes } from 'constants/enums/financialOptions';
import NotApplicable from 'components/Form/NotApplicable';
import { FinancialRepossessionItemModel } from 'forms/sections/financial/repossession/types';

interface Props extends Partial<FinancialRepossessionItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
}

export default class RepossessionItem extends ValidationElement<Props> {
  update = (queue: Partial<FinancialRepossessionItemModel>) => {
    this.props.onUpdate({
      LenderName: this.props.LenderName,
      PropertyType: this.props.PropertyType,
      PropertyTypeExplain: this.props.PropertyTypeExplain,
      FirstPastDue: this.props.FirstPastDue,
      AmountOwed: this.props.AmountOwed,
      NumberMissed: this.props.NumberMissed,
      Why: this.props.Why,
      When: this.props.When,
      WhenEstimated: this.props.WhenEstimated,
      AccountNum: this.props.AccountNum,
      AccountNumIdk: this.props.AccountNumIdk,
      Current: this.props.Current,
      WhenResolved: this.props.WhenResolved,
      ActionsTaken: this.props.ActionsTaken,
      ActionsTakenNone: this.props.ActionsTakenNone,
      ActionsTakenReason: this.props.ActionsTakenReason,
      CourtName: this.props.CourtName,
      CourtNameNotApplicable: this.props.CourtNameNotApplicable,
      CourtLocation: this.props.CourtLocation,
      OrderNum: this.props.OrderNum,
      OrderNumIdk: this.props.OrderNumIdk,
      ...queue,
    });
  };

  updateLenderName = (values: FinancialRepossessionItemModel['LenderName']) => {
    this.update({
      LenderName: values,
    });
  };

  updatePropertyType = (values: FinancialRepossessionItemModel['PropertyType']) => {
    this.update({
      PropertyType: values,
    });
  };

  updatePropertyTypeExplain = (values: FinancialRepossessionItemModel['PropertyTypeExplain']) => {
    this.update({
      PropertyTypeExplain: values,
    });
  };

  updateFirstPastDue = (values: FinancialRepossessionItemModel['FirstPastDue']) => {
    this.update({
      FirstPastDue: values,
    });
  };

  updateNumberMissed = (values: FinancialRepossessionItemModel['NumberMissed']) => {
    this.update({
      NumberMissed: values,
    });
  };

  updateWhy = (values: FinancialRepossessionItemModel['Why']) => {
    this.update({
      Why: values,
    });
  };

  updateWhen = (values: FinancialRepossessionItemModel['When']) => {
    this.update({
      When: values,
    });
  };

  updateWhenEstimated = (values: FinancialRepossessionItemModel['WhenEstimated']) => {
    this.update({
      WhenEstimated: values,
    });
  };

  updateAccountNum = (values: FinancialRepossessionItemModel['AccountNum']) => {
    this.update({
      AccountNum: values,
    });
  };

  updateCurrent = (values: FinancialRepossessionItemModel['Current']) => {
    this.update({
      Current: values,
    });
  };

  updateWhenResolved = (values: FinancialRepossessionItemModel['WhenResolved']) => {
    this.update({
      WhenResolved: values,
    });
  };

  updateCourtName = (values: FinancialRepossessionItemModel['CourtName']) => {
    this.update({
      CourtName: values,
    });
  };

  updateCourtNameNotApplicable = (values: FinancialRepossessionItemModel['CourtNameNotApplicable']) => {
    this.update({
      CourtNameNotApplicable: values,
    });
  };

  updateCourtLocation = (values: FinancialRepossessionItemModel['CourtLocation']) => {
    this.update({
      CourtLocation: values,
    });
  };

  updateOrderNum = (values: FinancialRepossessionItemModel['OrderNum']) => {
    this.update({
      OrderNum: values,
    });
  };

  updateOrderNumIdk = (values: FinancialRepossessionItemModel['OrderNumIdk']) => {
    this.update({
      OrderNumIdk: values,
    });
  };

  updateAmountOwed = (values: FinancialRepossessionItemModel['AmountOwed']) => {
    this.update({
      AmountOwed: values,
    });
  };

  updateAccountNumIdk = (values: FinancialRepossessionItemModel['AccountNumIdk']) => {
    this.update({
      AccountNumIdk: values,
    });
  };

  updateActionsTaken = (values: FinancialRepossessionItemModel['ActionsTaken']) => {
    this.update({
      ActionsTaken: values,
    });
  };

  updateActionsTakenReason = (values: FinancialRepossessionItemModel['ActionsTakenReason']) => {
    this.update({
      ActionsTakenReason: values,
    });
  };

  updateActionsTakenNone = (values: FinancialRepossessionItemModel['ActionsTakenNone']) => {
    this.update({
      ActionsTakenNone: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div className="repossession-item">
        <Field
          title={i18n.t('financial.pvq.repossession.branch.ifYes.lenderNameText')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.LenderName`}
        >
          <Text
            {...this.props.LenderName}
            attribute={`${attribute}.LenderName`}
            name="lendername"
            onUpdate={this.updateLenderName}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.repossession.branch.ifYes.propertyTypeRadioGroup.title')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.PropertyType`}
        >
          <RadioGroup
            {...this.props.PropertyType}
            className="option-list option-list-vertical"
            selectedValue={(this.props.PropertyType || {}).value}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.PropertyTypeRadioGroup`}
          >
            {repossessedPropertyTypes.map((propertyType) => (
              <Radio
                key={propertyType}
                label={propertyType}
                value={propertyType}
                onUpdate={this.updatePropertyType}
              />
            ))}
          </RadioGroup>
        </Field>
        {this.props.PropertyType?.value.startsWith('Other') && (
          <Field
            title={i18n.t('financial.pvq.repossession.branch.ifYes.propertyTypeRadioGroup.ifOther')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.PropertyTypeExplain`}
          >
            <Textarea
              {...this.props.PropertyTypeExplain}
              attribute={`${attribute}.PropertyTypeExplain`}
              name="propertytypeexplain"
              onUpdate={this.updatePropertyTypeExplain}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.repossession.branch.ifYes.missedPayments.whenFirstDateEstimated')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.FirstPastDue`}
        >
          <DateControl
            {...this.props.FirstPastDue}
            attribute={`${attribute}.FirstPastDue`}
            name="firstpastdue"
            onUpdate={this.updateFirstPastDue}
            hideDay
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.repossession.branch.ifYes.missedPayments.numberMissedText')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.NumberMissed`}
        >
          <Text
            {...this.props.NumberMissed}
            attribute={`${attribute}.NumberMissed`}
            name="nummissed"
            onUpdate={this.updateNumberMissed}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.repossession.branch.ifYes.amountOwedText')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AmountOwed`}
        >
          <Currency
            {...this.props.AmountOwed}
            attribute={`${attribute}.AmountOwed`}
            name="AmountOwed"
            onUpdate={this.updateAmountOwed}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.repossession.branch.ifYes.whyTextArea')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Why`}
        >
          <Textarea
            {...this.props.Why}
            className="option-list option-list-vertical"
            selectedValue={(this.props.Why || {}).value}
            required={this.props.required}
            onUpdate={this.updateWhy}
            onError={this.props.onError}
            attribute={`${attribute}.Why`}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.repossession.branch.ifYes.whenDateEstimated')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.When`}
          exactAttribute
        >
          <DateControl
            {...this.props.When}
            attribute={`${attribute}.When`}
            name="when"
            onUpdate={this.updateWhen}
            hideDay
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.repossession.branch.ifYes.accountNumText')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AccountNum`}
        >
          <NotApplicable
            name="accountnum-idk"
            label={i18n.t('financial.pvq.shared.idk')}
            {...this.props.AccountNumIdk}
            onUpdate={this.updateAccountNumIdk}
            onError={this.props.onError}
            attribute={`${this.props.attribute}.AccountNumIdk`}
          >
            <Text
              {...this.props.AccountNum}
              className="option-list option-list-vertical"
              selectedValue={(this.props.AccountNum || {}).value}
              required={this.props.required}
              onUpdate={this.updateAccountNum}
              onError={this.props.onError}
              attribute={`${attribute}.AccountNum`}
            />
          </NotApplicable>
        </Field>

        <Branch
          {...this.props.Current}
          name="current"
          label={i18n.t('financial.pvq.repossession.branch.ifYes.branch.title')}
          labelSize="h4"
          className="repossession-current-branch"
          onUpdate={this.updateCurrent}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.handleError}
          attribute={`${attribute}.Current`}
        />

        {this.props.Current?.value === 'No' && (
          <Field
            title={i18n.t('financial.pvq.repossession.branch.ifYes.branch.ifNo.whenClosedDateEstimated')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.WhenResolved`}
            exactAttribute
          >
            <DateControl
              {...this.props.WhenResolved}
              attribute={`${attribute}.WhenResolved`}
              name="whenresolved"
              onUpdate={this.updateWhenResolved}
              hideDay
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              showEstimated={true}
            />
          </Field>
        )}

        {this.props.Current?.value === 'Yes' && (
          <Field
            title={i18n.t('financial.pvq.repossession.branch.ifYes.branch.ifYes.combobox.actionsTaken')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTaken`}
          >
            <NotApplicable
              {...this.props.ActionsTakenNone}
              label={i18n.t('financial.pvq.shared.none')}
              attribute={`${attribute}.ActionsTaken`}
              onUpdate={this.updateActionsTakenNone}
              scrollIntoView={this.props.scrollIntoView}
              required={this.props.required}
            >
              <Textarea
                {...this.props.ActionsTaken}
                attribute={`${attribute}.ActionsTaken`}
                scrollIntoView={this.props.scrollIntoView}
                onError={this.props.onError}
                onUpdate={this.updateActionsTaken}
                required={this.props.required}
              />
            </NotApplicable>
          </Field>
        )}

        {this.props.ActionsTakenNone?.applicable === false && this.props.Current?.value === 'Yes' && (
          <Field
            title={i18n.t('financial.pvq.repossession.branch.ifYes.branch.ifYes.combobox.ifNone')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTaken`}
          >
            <Textarea
              {...this.props.ActionsTakenReason}
              attribute={`${attribute}.ActionsTaken`}
              scrollIntoView={this.props.scrollIntoView}
              onUpdate={this.updateActionsTakenReason}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.repossession.branch.ifYes.courtNameTextNotApplicable')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtName`}
        >
          <NotApplicable
            {...this.props.CourtNameNotApplicable}
            label={i18n.t('financial.pvq.shared.na')}
            onUpdate={this.updateCourtNameNotApplicable}
            attribute={`${attribute}.CourtNameNotApplicable`}
          >
            <Text
              {...this.props.CourtName}
              attribute={`${attribute}.CourtName`}
              name="courtname"
              onUpdate={this.updateCourtName}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </NotApplicable>
        </Field>

        {this.props.CourtNameNotApplicable?.applicable !== false && (
          <>
            <Field
              title={i18n.t('financial.pvq.repossession.branch.ifYes.courtLocation')}
              titleSize="h4"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CourtLocation`}
            >
              <PVQLocation
                {...this.props.CourtLocation}
                attribute={`${attribute}.CourtLocation`}
                name="courtlocation"
                layout={Layout.US_STREET_CITY_STATE_ZIP}
                onUpdate={this.updateCourtLocation}
                onError={this.props.onError}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
              />
            </Field>

            <Field
              title={i18n.t('financial.pvq.repossession.branch.ifYes.orderNumTextIdk')}
              titleSize="h4"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.OrderNum`}
            >
              <NotApplicable
                {...this.props.OrderNumIdk}
                name="ordernumidk"
                label={i18n.t('financial.pvq.shared.idk')}
                onUpdate={this.updateOrderNumIdk}
                onError={this.props.onError}
                attribute={`${attribute}.OrderNumIdk`}
              >
                <Text
                  {...this.props.OrderNum}
                  attribute={`${attribute}.OrderNum`}
                  name="ordernum"
                  onUpdate={this.updateOrderNum}
                  onError={this.props.onError}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                />
              </NotApplicable>
            </Field>
          </>
        )}
      </div>
    );
  }
}
RepossessionItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
  required: false,
};
